(() => {
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const shopRoot = () => window.Shopify?.routes?.root || '/';

  const setExpanded = (button, open) => button?.setAttribute('aria-expanded', String(open));
  const showDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.body.classList.add('dialog-open');
  };
  const closeDialog = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.body.classList.remove('dialog-open');
  };

  const updateCartCount = (count) => {
    qsa('[data-cart-count]').forEach((bubble) => {
      bubble.textContent = count;
      bubble.classList.toggle('is-hidden', Number(count) === 0);
    });
  };

  const replaceCartDrawer = (html, openAfter = false) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector('[data-cart-drawer]');
    if (!incoming) return;

    const current = qs('[data-cart-drawer]');
    if (current) current.replaceWith(incoming);
    else document.body.appendChild(incoming);

    const drawers = qsa('[data-cart-drawer]');
    drawers.slice(1).forEach((drawer) => drawer.remove());

    if (openAfter) showDialog(qs('[data-cart-drawer]'));
  };

  const replaceCartPage = (html) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const incoming = doc.querySelector('[data-cart-page]');
    const current = qs('[data-cart-page]');
    if (incoming && current) current.replaceWith(incoming);
  };

  const syncCartDrawer = async (openAfter = false) => {
    const response = await fetch(`${shopRoot()}cart`, {
      headers: { Accept: 'text/html' }
    });
    if (!response.ok) throw new Error('Unable to refresh cart drawer');
    const html = await response.text();
    replaceCartDrawer(html, openAfter);
    return fetchCart();
  };

  const fetchCart = async () => {
    const response = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    const cart = await response.json();
    updateCartCount(cart.item_count);
    return cart;
  };

  const addVariant = async (variantId, quantity = 1) => {
    const response = await fetch(`${shopRoot()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: Number(quantity) }],
        sections: ['cart-drawer'],
        sections_url: window.location.pathname
      })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to add item');
    replaceCartDrawer(data.sections?.['cart-drawer'], true);
    await fetchCart();
    return data;
  };

  const changeCartLine = async (line, quantity) => {
    const cartPage = qs('[data-cart-page]');
    const cartPageSection = cartPage?.dataset.sectionId;
    const drawer = qs('[data-cart-drawer]');
    const drawerWasOpen = Boolean(drawer?.open || drawer?.hasAttribute('open'));
    const sections = ['cart-drawer'];
    if (cartPageSection) sections.push(cartPageSection);

    const response = await fetch(`${shopRoot()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: Number(line),
        quantity: Number(quantity),
        sections,
        sections_url: window.location.pathname
      })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to update cart');
    replaceCartDrawer(data.sections?.['cart-drawer'], drawerWasOpen);
    if (cartPageSection) replaceCartPage(data.sections?.[cartPageSection]);
    updateCartCount(data.item_count);
    return data;
  };

  const updateCartNote = async (note) => {
    const response = await fetch(`${shopRoot()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to save cart note');
    updateCartCount(data.item_count);
    return data;
  };

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
  let mobileMenuOpener = null;
  let searchDrawerOpener = null;

  const trapFocus = (container, event) => {
    if (!container || event.key !== 'Tab') return;
    const items = qsa(focusableSelector, container).filter((item) => item.getClientRects().length > 0 && item.getAttribute('aria-hidden') !== 'true');
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const closeMobileNav = (restoreFocus = true) => {
    const nav = qs('[data-mobile-nav]');
    if (!nav || !nav.classList.contains('is-open')) return false;
    nav.classList.remove('is-open');
    nav.setAttribute('aria-hidden', 'true');
    setExpanded(qs('[data-mobile-toggle]'), false);
    document.body.classList.remove('menu-open');
    if (restoreFocus) mobileMenuOpener?.focus();
    return true;
  };

  const closeSearchDrawer = (restoreFocus = true) => {
    const drawer = qs('[data-search-drawer]');
    if (!drawer || !drawer.classList.contains('is-open')) return false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    qsa('[data-search-toggle]').forEach((button) => setExpanded(button, false));
    if (restoreFocus) searchDrawerOpener?.focus();
    return true;
  };

  qsa('[data-mobile-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const nav = qs('[data-mobile-nav]');
      if (!nav) return;
      const open = !nav.classList.contains('is-open');
      if (!open) {
        closeMobileNav(false);
        return;
      }
      mobileMenuOpener = button;
      nav.classList.add('is-open');
      nav.setAttribute('aria-hidden', 'false');
      setExpanded(button, true);
      document.body.classList.add('menu-open');
      rotateMenuFeatures(nav);
      window.requestAnimationFrame(() => qs('[data-mobile-close]', nav)?.focus());
    });
  });

  qsa('[data-mobile-close]').forEach((button) => {
    button.addEventListener('click', () => closeMobileNav());
  });

  qsa('[data-search-toggle]').forEach((button) => {
    button.addEventListener('click', () => {
      const drawer = qs('[data-search-drawer]');
      if (!drawer) return;
      const open = !drawer.classList.contains('is-open');
      if (!open) {
        closeSearchDrawer(false);
        return;
      }
      searchDrawerOpener = button;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      qsa('[data-search-toggle]').forEach((toggle) => setExpanded(toggle, toggle === button));
      setTimeout(() => qs('input[type="search"]', drawer)?.focus(), 50);
    });
  });

  qsa('[data-search-close]').forEach((button) => {
    button.addEventListener('click', () => closeSearchDrawer());
  });

  document.addEventListener('keydown', (event) => {
    const mobileNav = qs('[data-mobile-nav].is-open');
    const searchDrawer = qs('[data-search-drawer].is-open');
    if (searchDrawer) trapFocus(searchDrawer, event);
    else if (mobileNav) trapFocus(mobileNav, event);
  });

  function initStickyHeaders(scope = document) {
    qsa('[data-sticky-header="scroll-up"]', scope).forEach((header) => {
      if (header.dataset.scrollInitialized === 'true') return;
      header.dataset.scrollInitialized = 'true';
      let lastY = window.scrollY;
      const onScroll = () => {
        const currentY = window.scrollY;
        const scrollingDown = currentY > lastY;
        header.classList.toggle('is-scrolled-away', scrollingDown && currentY > header.offsetHeight * 2);
        lastY = Math.max(0, currentY);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    });
  }

  initStickyHeaders();

  let predictiveTimer;
  qsa('[data-predictive-input]').forEach((input) => {
    input.addEventListener('input', () => {
      clearTimeout(predictiveTimer);
      const target = qs('[data-predictive-results]', input.closest('[data-search-drawer]'));
      const term = input.value.trim();
      if (!target || term.length < 2) {
        if (target) target.innerHTML = '';
        return;
      }
      predictiveTimer = setTimeout(async () => {
        try {
          const url = `${shopRoot()}search/suggest?q=${encodeURIComponent(term)}&resources[type]=product,collection,query,page,article&resources[limit]=6&section_id=predictive-search`;
          const response = await fetch(url);
          const text = await response.text();
          const doc = new DOMParser().parseFromString(text, 'text/html');
          target.innerHTML = doc.querySelector('.shopify-section')?.innerHTML || '';
        } catch (_) {
          target.innerHTML = '';
        }
      }, 180);
    });
  });

  function activateProductMedia(section, mediaId) {
    if (!section || !mediaId) return;
    const gallery = qs('[data-product-gallery]', section);
    if (!gallery) return;
    const items = qsa('[data-media-id]', gallery);
    const target = items.find((item) => String(item.dataset.mediaId) === String(mediaId));
    if (!target) return;

    const editorialMode = gallery.dataset.galleryMode === 'editorial';
    items.forEach((item) => {
      const active = item === target;
      item.classList.toggle('is-active', active);
      item.hidden = editorialMode ? false : !active;
      if (!active) {
        qsa('video', item).forEach((video) => video.pause());
        qsa('iframe', item).forEach((frame) => {
          const src = frame.getAttribute('src');
          if (src) frame.setAttribute('src', src);
        });
      }
    });
    if (editorialMode && window.matchMedia('(max-width: 749px)').matches) {
      target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    qsa('[data-gallery-thumb]', section).forEach((thumb) => {
      const active = String(thumb.dataset.targetMedia) === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });

    const current = qs('[data-gallery-current]', section);
    if (current) current.textContent = String(items.indexOf(target) + 1);
  }

  function stepProductMedia(section, direction) {
    if (!section) return;
    const gallery = qs('[data-product-gallery]', section);
    const items = qsa('[data-media-id]', gallery);
    if (items.length < 2) return;
    let index = items.findIndex((item) => item.classList.contains('is-active'));
    if (index < 0) index = 0;
    index = (index + direction + items.length) % items.length;
    activateProductMedia(section, items[index].dataset.mediaId);
  }

  function initProductGallery(section) {
    if (!section || section.dataset.galleryInitialized === 'true') return;
    const gallery = qs('[data-product-gallery]', section);
    if (!gallery) return;
    section.dataset.galleryInitialized = 'true';

    qsa('[data-gallery-thumb]', section).forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        activateProductMedia(section, thumb.dataset.targetMedia);
      });
    });

    qs('[data-gallery-prev]', section)?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      stepProductMedia(section, -1);
    });

    qs('[data-gallery-next]', section)?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      stepProductMedia(section, 1);
    });
  }

  function initProduct(section) {
    if (!section || section.dataset.productInitialized === 'true') return;
    const dataNode = qs('[data-variant-data]', section);
    if (!dataNode) return;
    let variants = [];
    try { variants = JSON.parse(dataNode.textContent); } catch (_) { return; }
    section.dataset.productInitialized = 'true';

    const form = qs('form.product-form-root', section);
    const idInput = qs('[data-variant-id]', section);
    const add = qs('[data-add-to-cart]', section);
    const price = qs('[data-product-price]', section);
    const compare = qs('[data-compare-price]', section);
    const unit = qs('[data-unit-price]', section);
    const pickup = qs('[data-pickup-container]', section);
    const quantity = qs('[data-quantity] input', section);
    const planSelect = qs('select[name="selling_plan"]', section);
    const sticky = qs('[data-sticky-buy]', section);
    const stickyPrice = qs('[data-sticky-price]', section);
    const stickyVariant = qs('[data-sticky-variant]', section);
    const stickySubmit = qs('[data-sticky-submit]', section);
    const sku = qs('[data-product-sku]', section);
    const skuRow = sku?.closest('.product-sku');
    const fulfilment = qs('[data-fulfilment]', section);
    const stockCounter = qs('[data-stock-counter]', section);
    const giftWrap = qs('[data-gift-wrap]', form || section);
    const paymentButton = qs('.shopify-payment-button', form || section);

    const selectedOptions = () => [
      ...qsa('[data-option-value]:checked', section).map((input) => ({ position: Number(input.dataset.optionPosition), value: input.value })),
      ...qsa('[data-option-select]', section).map((select) => ({ position: Number(select.dataset.optionPosition), value: select.value }))
    ].sort((a, b) => a.position - b.position).map((item) => item.value);

    const matchVariant = () => variants.find((variant) => JSON.stringify(variant.options) === JSON.stringify(selectedOptions())) || (variants.length === 1 ? variants[0] : null);

    const optionAvailable = (position, candidate) => {
      const selected = selectedOptions();
      return variants.some((variant) => variant.available && variant.options[position] === candidate && variant.options.slice(0, position).every((value, index) => !selected[index] || value === selected[index]));
    };

    const updateOptionAvailability = () => {
      qsa('[data-option-value]', section).forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        const available = optionAvailable(position, input.value);
        input.disabled = !available;
        const label = input.id ? section.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
        label?.classList.toggle('is-unavailable', !available);
      });
      qsa('[data-option-select]', section).forEach((select) => {
        const position = Number(select.dataset.optionPosition) - 1;
        Array.from(select.options).forEach((option) => { option.disabled = !optionAvailable(position, option.value); });
      });
    };

    const renderVariant = async (variant) => {
      if (!variant) {
        if (add) { add.disabled = true; add.textContent = add.dataset.unavailableText; }
        if (stickySubmit) { stickySubmit.disabled = true; stickySubmit.textContent = add?.dataset.unavailableText || ''; }
        return;
      }
      if (idInput) {
        const changedVariant = String(idInput.value) !== String(variant.id);
        idInput.value = variant.id;
        if (changedVariant) idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (quantity) {
        quantity.min = variant.quantity_min || 1;
        quantity.step = variant.quantity_increment || 1;
        if (variant.quantity_max) quantity.max = variant.quantity_max; else quantity.removeAttribute('max');
        if (Number(quantity.value) < Number(quantity.min)) quantity.value = quantity.min;
      }
      if (planSelect) {
        const previous = planSelect.value;
        const oneTimeLabel = planSelect.dataset.oneTimeLabel || '';
        planSelect.innerHTML = '';
        const oneTime = document.createElement('option');
        oneTime.value = ''; oneTime.textContent = oneTimeLabel; planSelect.appendChild(oneTime);
        variant.selling_plans.forEach((plan) => {
          const option = document.createElement('option');
          option.value = plan.id; option.textContent = plan.name;
          if (String(plan.id) === String(previous)) option.selected = true;
          planSelect.appendChild(option);
        });
      }
      if (price) price.textContent = variant.price;
      if (compare) { compare.textContent = variant.compare_at_price || ''; compare.classList.toggle('is-hidden', !variant.compare_at_price); }
      if (unit) { unit.textContent = variant.unit_price ? `${variant.unit_price} / ${variant.unit_reference}` : ''; unit.classList.toggle('is-hidden', !variant.unit_price); }
      const availableText = fulfilment?.dataset.buttonLabel || add?.dataset.availableText || '';
      if (add) { add.disabled = !variant.available; add.textContent = variant.available ? availableText : add.dataset.soldoutText; }
      if (stickyPrice) stickyPrice.textContent = variant.price;
      if (stickyVariant) stickyVariant.textContent = variant.options.join(' / ');
      if (stickySubmit) { stickySubmit.disabled = !variant.available; stickySubmit.textContent = variant.available ? availableText : add?.dataset.soldoutText || ''; }
      if (stockCounter) {
        const tracked = Boolean(variant.inventory_management);
        const quantityAvailable = Number(variant.inventory_quantity || 0);
        const threshold = Number(stockCounter.dataset.lowThreshold || 3);
        const showExact = stockCounter.dataset.showExact === 'true';
        let stockText = '';
        let low = false;
        if (tracked && quantityAvailable > 0) {
          low = quantityAvailable <= threshold;
          if (low) stockText = (stockCounter.dataset.lowStockTemplate || '').replace('__COUNT__', String(quantityAvailable));
          else if (showExact) stockText = (stockCounter.dataset.exactTemplate || '').replace('__COUNT__', String(quantityAvailable));
          else stockText = stockCounter.dataset.inStockTemplate || '';
        } else if (tracked && variant.available && variant.inventory_policy === 'continue') {
          stockText = stockCounter.dataset.orderTemplate || '';
        }
        stockCounter.textContent = stockText;
        stockCounter.hidden = !stockText;
        stockCounter.classList.toggle('is-low', low);
      }
      if (sku) sku.textContent = variant.sku || '';
      if (skuRow) skuRow.classList.toggle('is-hidden', !variant.sku);
      activateProductMedia(section, variant.featured_media_id);
      if (section.dataset.updateUrl !== 'false' && history.replaceState) {
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
      }
      if (pickup) {
        try {
          const response = await fetch(`${shopRoot()}variants/${variant.id}/?section_id=${pickup.dataset.sectionId}`);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          pickup.innerHTML = doc.querySelector('.shopify-section')?.innerHTML || '';
        } catch (_) { pickup.innerHTML = ''; }
      }
    };

    const onOptionChange = () => {
      const variant = matchVariant();
      renderVariant(variant);
      updateOptionAvailability();
    };
    qsa('[data-option-value]', section).forEach((input) => input.addEventListener('change', onOptionChange));
    qsa('[data-option-select]', section).forEach((select) => select.addEventListener('change', onOptionChange));
    const initial = variants.find((variant) => String(variant.id) === String(idInput?.value)) || matchVariant();
    updateOptionAvailability();
    if (initial) renderVariant(initial);

    if (section.dataset.mobileSwipe === 'true') {
      const stage = qs('.product-gallery__stage', section);
      let touchStartX = null;
      stage?.addEventListener('touchstart', (event) => {
        if (event.touches.length === 1) touchStartX = event.touches[0].clientX;
      }, { passive: true });
      stage?.addEventListener('touchend', (event) => {
        if (touchStartX === null || !event.changedTouches.length) return;
        const delta = event.changedTouches[0].clientX - touchStartX;
        touchStartX = null;
        if (Math.abs(delta) < 45) return;
        stepProductMedia(section, delta < 0 ? 1 : -1);
      }, { passive: true });
    }

    if (planSelect) planSelect.addEventListener('change', () => {
      const variant = variants.find((item) => String(item.id) === String(idInput?.value));
      const plan = variant?.selling_plans.find((item) => String(item.id) === String(planSelect.value));
      const shownPrice = plan?.price || variant?.price || price?.textContent;
      if (price && shownPrice) price.textContent = shownPrice;
      if (stickyPrice && shownPrice) stickyPrice.textContent = shownPrice;
    });

    if (giftWrap && paymentButton) {
      const syncGiftWrapCheckout = () => { paymentButton.hidden = Boolean(giftWrap.checked && giftWrap.dataset.giftWrapVariant); };
      giftWrap.addEventListener('change', syncGiftWrapCheckout);
      syncGiftWrapCheckout();
    }

    if (stickySubmit && form) stickySubmit.addEventListener('click', () => form.requestSubmit());
    if (sticky && add && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => { sticky.hidden = entries[0].isIntersecting; }, { threshold: 0.2 });
      observer.observe(add);
    }
  }

  qsa('[data-product-section]').forEach((section) => {
    initProductGallery(section);
    try { initProduct(section); } catch (_) {}
  });

  const cartItemsFromProductForm = (form) => {
    const data = new FormData(form);
    const quantity = Math.max(1, Number(data.get('quantity') || 1));
    const item = { id: Number(data.get('id')), quantity };
    const sellingPlan = data.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    const properties = {};
    data.forEach((value, key) => {
      const match = key.match(/^properties\[(.*)\]$/);
      if (match && String(value).trim() !== '') properties[match[1]] = String(value);
    });
    if (Object.keys(properties).length) item.properties = properties;
    const items = [item];
    const wrap = qs('[data-gift-wrap]:checked', form);
    const wrapVariant = Number(wrap?.dataset.giftWrapVariant || 0);
    if (wrapVariant) {
      items.push({
        id: wrapVariant,
        quantity: wrap.dataset.giftWrapQuantityMode === 'match' ? quantity : 1,
        properties: { _Faceted_service: 'Gift wrapping' }
      });
    }
    return items;
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest?.('form.product-form-root');
    if (!form) return;
    if (event.submitter && event.submitter.name && event.submitter.name !== 'add') return;
    event.preventDefault();
    const drawer = qs('[data-cart-drawer]');
    const button = qs('[data-add-to-cart]', form);
    const error = qs('[data-product-error]', form);
    if (button) button.setAttribute('aria-busy', 'true');
    if (error) { error.hidden = true; error.textContent = ''; }
    try {
      const payload = { items: cartItemsFromProductForm(form) };
      if (drawer) {
        payload.sections = ['cart-drawer'];
        payload.sections_url = window.location.pathname;
      }
      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) {
        if (drawer) {
          try { await syncCartDrawer(true); } catch (_) {}
        }
        throw new Error(data.description || data.message || 'Unable to add item');
      }
      if (drawer) {
        replaceCartDrawer(data.sections?.['cart-drawer'], true);
        await fetchCart();
        closeDialog(qs('[data-quick-buy-dialog]'));
      } else {
        window.location.assign(`${shopRoot()}cart`);
      }
    } catch (err) {
      if (error) { error.textContent = err.message; error.hidden = false; }
    } finally {
      if (button) button.removeAttribute('aria-busy');
    }
  });

  function initSlideshows(scope = document) {
    qsa('[data-slideshow]', scope).forEach((root) => {
      if (root.dataset.initialized === 'true') return;
      root.dataset.initialized = 'true';
      const slides = qsa('[data-slide]', root);
      const dots = qsa('[data-slide-dot]', root);
      if (!slides.length) return;
      let current = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      const show = (index) => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => slide.classList.toggle('is-active', i === current));
        dots.forEach((dot, i) => dot.setAttribute('aria-current', i === current ? 'true' : 'false'));
      };
      qs('[data-slide-prev]', root)?.addEventListener('click', () => show(current - 1));
      qs('[data-slide-next]', root)?.addEventListener('click', () => show(current + 1));
      dots.forEach((dot) => dot.addEventListener('click', () => show(Number(dot.dataset.slideDot))));
      if (root.dataset.autoplay === 'true' && slides.length > 1) {
        let timer;
        const start = () => { clearInterval(timer); timer = setInterval(() => show(current + 1), Number(root.dataset.interval) || 6000); };
        const stop = () => clearInterval(timer);
        root.addEventListener('mouseenter', stop); root.addEventListener('mouseleave', start); root.addEventListener('focusin', stop); root.addEventListener('focusout', start); start();
      }
    });
  }

  function initBeforeAfter(scope = document) {
    qsa('[data-before-after]', scope).forEach((root) => {
      const input = qs('[data-before-after-range]', root);
      const overlay = qs('[data-before-after-overlay]', root);
      if (!input || !overlay) return;
      const update = () => { overlay.style.clipPath = `inset(0 ${100 - Number(input.value)}% 0 0)`; };
      input.addEventListener('input', update); update();
    });
  }

  function initRecentlyViewed(scope = document) {
    const key = 'faceted_recent_products';
    qsa('[data-recently-viewed-section]', scope).forEach((section) => {
      const script = qs('[data-recent-product]', section);
      let items = [];
      try { items = JSON.parse(localStorage.getItem(key) || '[]'); } catch (_) { items = []; }
      if (script) {
        try {
          const current = JSON.parse(script.textContent);
          items = [current, ...items.filter((item) => String(item.id) !== String(current.id))].slice(0, 12);
          localStorage.setItem(key, JSON.stringify(items));
        } catch (_) {}
      }
      const currentId = section.dataset.currentProduct;
      const limit = Number(section.dataset.limit || 4);
      const grid = qs('[data-recently-viewed-grid]', section);
      if (!grid) return;
      const visible = items.filter((item) => String(item.id) !== String(currentId || '')).slice(0, limit);
      grid.replaceChildren();
      visible.forEach((item) => {
        const article = document.createElement('article'); article.className = 'product-card product-card--recent';
        const link = document.createElement('a'); link.className = 'product-card__media'; link.href = item.url;
        if (item.image) { const img = document.createElement('img'); img.src = item.image; img.alt = item.title; img.loading = 'lazy'; img.width = 720; img.height = 900; img.className = 'product-card__image'; link.appendChild(img); }
        const content = document.createElement('div'); content.className = 'product-card__content';
        const title = document.createElement('h3'); title.className = 'product-card__title'; const titleLink = document.createElement('a'); titleLink.href = item.url; titleLink.textContent = item.title; title.appendChild(titleLink);
        const price = document.createElement('div'); price.className = 'price'; price.textContent = item.price;
        content.append(title, price); article.append(link, content); grid.appendChild(article);
      });
      section.hidden = visible.length === 0;
    });
  }


  function initPersonalizationFonts(scope = document) {
    const getFontStack = (family, fallback) => {
      const fallbackStack = fallback || 'serif';
      if (!family) return fallbackStack;
      const safeFamily = family.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      return `"${safeFamily}", ${fallbackStack}`;
    };

    const syncCharacterCounter = (input) => {
      const card = input.closest('.personalization-card');
      const counter = qs('[data-personalization-count]', card);
      if (!counter) return;
      const max = Number(input.dataset.characterLimit || input.getAttribute('maxlength') || 0);
      const current = input.value.length;
      const template = counter.dataset.countTemplate || '__CURRENT__ / __MAX__ characters';
      counter.textContent = template
        .replaceAll('__CURRENT__', String(current))
        .replaceAll('__MAX__', String(max));
    };

    const syncCardSamples = (field, select) => {
      if (!select) return;
      const options = Array.from(select.options || []);

      qsa('[data-engraving-font-option]', field).forEach((button) => {
        const sample = qs('[data-engraving-font-option-sample]', button);
        if (!sample) return;

        const matchingOption = options.find((item) => item.value === button.dataset.value);
        if (!matchingOption) return;

        const family = matchingOption.dataset.fontFamily || '';
        const fallback = matchingOption.dataset.fontFallback || 'serif';
        const weight = matchingOption.dataset.fontWeight || '400';
        const style = matchingOption.dataset.fontStyle || 'normal';
        const stack = getFontStack(family, fallback);

        sample.style.fontFamily = stack;
        sample.style.fontWeight = weight;
        sample.style.fontStyle = style;

        if (family && document.fonts?.load) {
          document.fonts.load(`${style} ${weight} 24px "${family.replace(/"/g, '\"')}"`).catch(() => {});
        }
      });
    };

    const syncField = (field) => {
      const select = qs('[data-engraving-font-select]', field);
      const preview = qs('[data-engraving-font-preview]', field);
      const previewText = qs('[data-engraving-font-preview-text]', field);
      const card = field.closest('.personalization-card');
      const engraving = qs('[data-personalization-input]', card);
      if (!select || !preview || !previewText) return;

      if (!select.dataset.propertyName) select.dataset.propertyName = select.getAttribute('name') || '';
      if (engraving?.value.trim()) {
        if (select.dataset.propertyName) select.setAttribute('name', select.dataset.propertyName);
      } else {
        select.removeAttribute('name');
      }

      const option = select.selectedOptions[0];
      if (!option) return;
      syncCardSamples(field, select);

      const family = option.dataset.fontFamily || '';
      const fallback = option.dataset.fontFallback || 'serif';
      const weight = option.dataset.fontWeight || '400';
      const style = option.dataset.fontStyle || 'normal';
      const imageUrl = option.dataset.previewImage || '';
      const imageAlt = option.dataset.previewImageAlt || option.textContent.trim();
      const optionPreview = option.dataset.previewText || previewText.dataset.defaultPreview || previewText.textContent || '';
      const stack = getFontStack(family, fallback);

      if (!previewText.dataset.defaultPreview) previewText.dataset.defaultPreview = previewText.textContent || '';
      previewText.textContent = engraving?.value.trim() || optionPreview;
      preview.style.fontFamily = stack;
      preview.style.fontWeight = weight;
      preview.style.fontStyle = style;
      previewText.style.fontFamily = stack;
      previewText.style.fontWeight = weight;
      previewText.style.fontStyle = style;
      previewText.hidden = false;

      const existingImage = qs('[data-engraving-font-preview-image]', field);
      if (existingImage) existingImage.remove();

      if (imageUrl) {
        const image = document.createElement('img');
        image.dataset.engravingFontPreviewImage = '';
        image.alt = imageAlt;
        image.loading = 'eager';
        image.decoding = 'async';
        image.hidden = true;

        const revealImage = () => {
          const selected = select.selectedOptions[0];
          if (!image.isConnected || !selected || selected.dataset.previewImage !== imageUrl) return;
          if (image.naturalWidth > 0) {
            image.hidden = false;
            previewText.hidden = true;
          }
        };

        image.addEventListener('load', revealImage, { once: true });
        image.addEventListener('error', () => {
          image.remove();
          previewText.hidden = false;
        }, { once: true });
        preview.appendChild(image);
        image.src = imageUrl;
        if (image.complete) revealImage();
      } else if (family && document.fonts?.load) {
        document.fonts.load(`${style} ${weight} 32px "${family.replace(/"/g, '\\"')}"`).catch(() => {});
      }

      qsa('[data-engraving-font-option]', field).forEach((button) => {
        const selected = button.dataset.value === select.value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
    };

    qsa('[data-personalization-input]', scope).forEach((input) => {
      if (input.dataset.personalizationCounterBound !== 'true') {
        input.dataset.personalizationCounterBound = 'true';
        input.addEventListener('input', () => {
          syncCharacterCounter(input);
          const card = input.closest('.personalization-card');
          qsa('[data-engraving-font-field]', card).forEach(syncField);
        });
      }
      syncCharacterCounter(input);
    });

    qsa('[data-engraving-font-field]', scope).forEach((field) => {
      if (field.dataset.fontEventsBound !== 'true') {
        field.dataset.fontEventsBound = 'true';

        field.addEventListener('change', (event) => {
          if (event.target.matches('[data-engraving-font-select]')) syncField(field);
        });

        field.addEventListener('click', (event) => {
          const button = event.target.closest('[data-engraving-font-option]');
          if (!button || !field.contains(button)) return;
          const select = qs('[data-engraving-font-select]', field);
          if (!select) return;
          select.value = button.dataset.value || '';
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      syncField(field);

      if (document.fonts?.ready) {
        document.fonts.ready
          .then(() => syncCardSamples(field, qs('[data-engraving-font-select]', field)))
          .catch(() => {});
      }
    });
  }

  function initReveal(scope = document) {
    const candidates = qsa('.shopify-section > .section, .shopify-section > section', scope);
    const show = (item) => item.classList.add('reveal-item', 'is-visible');
    const shoppingGrid = '.collection-showcase, .collection-product-grid, [data-collection-directory]';
    const motionDisabled = document.body.classList.contains('motion--none') || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (motionDisabled || !('IntersectionObserver' in window)) {
      candidates.forEach(show);
      return;
    }
    document.documentElement.style.setProperty('--reveal-distance', `${Number(document.body.dataset.animationDistance || 16)}px`);
    let observer;
    try {
      observer = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          obs.unobserve(entry.target);
        });
      }, { rootMargin: '0px', threshold: 0 });
      candidates.forEach((item) => {
        // Shopping content must never depend on a decorative scroll animation.
        const rect = item.getBoundingClientRect();
        if (item.matches('.collection-directory-section') || item.querySelector(shoppingGrid) ||
            (rect.top < window.innerHeight && rect.bottom > 0)) {
          show(item);
          return;
        }
        if (item.classList.contains('reveal-item')) return;
        item.classList.add('reveal-item');
        observer.observe(item);
      });
    } catch (error) {
      observer?.disconnect();
      candidates.forEach(show);
    }
  }

  function initBackToTop(scope = document) {
    const button = qs('[data-back-to-top]', scope === document ? document : scope) || qs('[data-back-to-top]');
    if (!button || button.dataset.initialized === 'true') return;
    button.dataset.initialized = 'true';
    const sync = () => { button.hidden = window.scrollY < 700; };
    window.addEventListener('scroll', sync, { passive: true });
    button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
    sync();
  }

  function initPromoPopup() {
    const popup = qs('[data-promo-popup]');
    if (!popup || popup.dataset.initialized === 'true') return;
    popup.dataset.initialized = 'true';
    const storageKey = 'faceted:promo-popup:dismissed';
    const days = Math.max(1, Number(popup.dataset.days || 7));
    const dismissedAt = Number(localStorage.getItem(storageKey) || 0);
    const suppressed = dismissedAt && (Date.now() - dismissedAt) < days * 86400000;
    const dismiss = () => {
      localStorage.setItem(storageKey, String(Date.now()));
      closeDialog(popup);
    };
    qs('[data-promo-popup-close]', popup)?.addEventListener('click', dismiss);
    popup.addEventListener('cancel', (event) => { event.preventDefault(); dismiss(); });
    popup.addEventListener('click', (event) => { if (event.target === popup) dismiss(); });
    if (qs('.success-message', popup)) { showDialog(popup); return; }
    if (suppressed) return;
    let shown = false;
    const show = () => { if (shown || popup.open) return; shown = true; showDialog(popup); };
    const trigger = popup.dataset.trigger || 'delay';
    if (trigger === 'scroll') {
      const threshold = Math.max(5, Math.min(95, Number(popup.dataset.scroll || 45)));
      const onScroll = () => {
        const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        if ((window.scrollY / max) * 100 >= threshold) { window.removeEventListener('scroll', onScroll); show(); }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    } else if (trigger === 'exit') {
      const onExit = (event) => { if (event.clientY <= 0) { document.removeEventListener('mouseout', onExit); show(); } };
      document.addEventListener('mouseout', onExit);
    } else {
      window.setTimeout(show, Math.max(1, Number(popup.dataset.delay || 8)) * 1000);
    }
  }

  async function loadMoreProducts(anchor) {
    const pagination = anchor?.closest('[data-collection-pagination]');
    const results = pagination?.closest('.collection-results');
    const grid = qs('.collection-product-grid', results);
    if (!pagination || !grid || pagination.dataset.loading === 'true') return;
    pagination.dataset.loading = 'true';
    anchor.setAttribute('aria-busy', 'true');
    const status = qs('[data-load-more-status]', pagination);
    if (status) status.textContent = pagination.dataset.loadingLabel || '';
    try {
      const response = await fetch(anchor.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Unable to load products');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = qs('.collection-product-grid', doc);
      if (!incomingGrid) throw new Error('Unable to load products');
      [...incomingGrid.children].forEach((child) => grid.appendChild(child));
      const incomingPagination = qs('[data-collection-pagination]', doc);
      if (incomingPagination) {
        pagination.replaceWith(incomingPagination);
        initInfiniteScroll(results);
      } else {
        if (status) status.textContent = pagination.dataset.allLoadedLabel || '';
        anchor.remove();
        pagination.dataset.loading = 'false';
      }
      initReveal(results);
    } catch (_) {
      pagination.dataset.loading = 'false';
      anchor.removeAttribute('aria-busy');
      if (status) status.textContent = '';
    }
  }

  function initInfiniteScroll(scope = document) {
    qsa('[data-collection-pagination][data-mode="infinite"]', scope).forEach((pagination) => {
      if (pagination.dataset.observed === 'true') return;
      pagination.dataset.observed = 'true';
      const anchor = qs('[data-load-more]', pagination);
      if (!anchor) return;
      if (!('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver((entries, obs) => {
        if (!entries[0].isIntersecting) return;
        obs.disconnect();
        loadMoreProducts(anchor);
      }, { rootMargin: '500px 0px' });
      observer.observe(pagination);
    });
  }

  initSlideshows();
  initBeforeAfter();
  initRecentlyViewed();
  initPersonalizationFonts();
  initReveal();
  initBackToTop();
  initPromoPopup();
  initInfiniteScroll();

  document.addEventListener('click', async (event) => {
    const target = event.target;

    const cartOpen = target.closest('[data-cart-drawer-open]');
    if (cartOpen && qs('[data-cart-drawer]')) { event.preventDefault(); showDialog(qs('[data-cart-drawer]')); return; }
    if (target.closest('[data-cart-drawer-close]')) { closeDialog(target.closest('dialog')); return; }

    const quickAdd = target.closest('[data-quick-add]');
    if (quickAdd) {
      event.preventDefault(); quickAdd.setAttribute('aria-busy', 'true');
      try { await addVariant(quickAdd.dataset.variantId, 1); } catch (_) {} finally { quickAdd.removeAttribute('aria-busy'); }
      return;
    }

    const quickBuy = target.closest('[data-quick-buy]');
    if (quickBuy) {
      event.preventDefault();
      const dialog = qs('[data-quick-buy-dialog]'); const content = qs('[data-quick-buy-content]', dialog);
      if (!dialog || !content) return;
      quickBuy.setAttribute('aria-busy', 'true');
      try {
        const separator = quickBuy.dataset.quickBuyUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${quickBuy.dataset.quickBuyUrl}${separator}section_id=quick-buy`);
        const html = await response.text(); const doc = new DOMParser().parseFromString(html, 'text/html');
        content.innerHTML = doc.querySelector('.shopify-section')?.innerHTML || html;
        qsa('[data-product-section]', content).forEach((section) => { initProductGallery(section); try { initProduct(section); } catch (_) {} });
        initPersonalizationFonts(content);
        showDialog(dialog);
      } finally { quickBuy.removeAttribute('aria-busy'); }
      return;
    }
    if (target.closest('[data-quick-buy-close]')) { closeDialog(target.closest('dialog')); return; }

    const dialogOpen = target.closest('[data-dialog-open]');
    if (dialogOpen) { showDialog(document.getElementById(dialogOpen.dataset.dialogOpen)); return; }
    if (target.closest('[data-dialog-close]')) { closeDialog(target.closest('dialog')); return; }

    const mediaOpen = target.closest('[data-media-open]');
    if (mediaOpen) {
      const section = mediaOpen.closest('[data-product-section]'); const dialog = qs('[data-media-modal]', section); const image = qs('[data-media-modal-image]', dialog);
      if (dialog && image) {
        const fitMediaModalImage = () => {
          const naturalWidth = image.naturalWidth || Number(image.getAttribute('width')) || 1;
          const naturalHeight = image.naturalHeight || Number(image.getAttribute('height')) || 1;
          const ratio = naturalWidth / naturalHeight;
          const maxWidth = Math.max(240, window.innerWidth * 0.92);
          const maxHeight = Math.max(240, window.innerHeight * 0.90);
          let width = maxWidth;
          let height = width / ratio;
          if (height > maxHeight) { height = maxHeight; width = height * ratio; }
          image.style.setProperty('width', Math.round(width) + 'px', 'important');
          image.style.setProperty('height', Math.round(height) + 'px', 'important');
        };
        image.onload = fitMediaModalImage;
        image.src = mediaOpen.dataset.fullImage;
        image.alt = mediaOpen.dataset.alt || '';
        showDialog(dialog);
        if (image.complete) requestAnimationFrame(fitMediaModalImage);
      }
      return;
    }
    if (target.closest('[data-media-modal-close]')) { closeDialog(target.closest('dialog')); return; }

    const swatch = target.closest('[data-card-swatch]');
    if (swatch) {
      const card = swatch.closest('[data-product-card]');
      qsa('[data-card-swatch]', card).forEach((item) => { const active = item === swatch; item.classList.toggle('is-selected', active); item.setAttribute('aria-pressed', String(active)); });
      card.classList.add('has-selected-swatch');
      const image = qs('[data-card-primary]', card);
      if (image && swatch.dataset.cardImage) { image.src = swatch.dataset.cardImage; image.removeAttribute('srcset'); if (swatch.dataset.cardImageWidth) image.width = Number(swatch.dataset.cardImageWidth); if (swatch.dataset.cardImageHeight) image.height = Number(swatch.dataset.cardImageHeight); }
      if (swatch.dataset.cardUrl) qsa('[data-card-link]', card).forEach((link) => { link.href = swatch.dataset.cardUrl; });
      const price = qs('[data-card-price] .price__current', card); if (price && swatch.dataset.cardPrice) price.textContent = swatch.dataset.cardPrice;
      const priceWrapper = qs('[data-card-price] [data-price-wrapper]', card);
      const comparePrice = qs('[data-card-price] [data-compare-price]', card);
      if (comparePrice) {
        const compareValue = swatch.dataset.cardComparePrice || '';
        comparePrice.textContent = compareValue;
        comparePrice.classList.toggle('is-hidden', !compareValue);
      }
      priceWrapper?.classList.toggle('price--sale', swatch.dataset.cardOnSale === 'true');
      const unitPrice = qs('[data-card-price] [data-unit-price]', card);
      if (unitPrice) {
        const value = swatch.dataset.cardUnitPrice || '';
        const reference = swatch.dataset.cardUnitReference || '';
        unitPrice.textContent = value ? `${value} / ${reference}` : '';
        unitPrice.classList.toggle('is-hidden', !value);
      }
      qs('[data-card-price] .price__from', card)?.remove();
      const quickBuyButton = qs('[data-quick-buy]', card); if (quickBuyButton && swatch.dataset.cardUrl) quickBuyButton.dataset.quickBuyUrl = swatch.dataset.cardUrl;
      const quickAddButton = qs('[data-quick-add]', card);
      if (quickAddButton && swatch.dataset.cardVariantId) {
        quickAddButton.dataset.variantId = swatch.dataset.cardVariantId;
        const variantAvailable = swatch.dataset.cardAvailable !== 'false';
        quickAddButton.disabled = !variantAvailable;
        quickAddButton.setAttribute('aria-disabled', String(!variantAvailable));
      }
      return;
    }

    const quantityStep = target.closest('[data-quantity] [data-step]');
    if (quantityStep) {
      const wrapper = quantityStep.closest('[data-quantity]'); const input = qs('input', wrapper); if (!input) return;
      const step = Number(input.step || 1) * Number(quantityStep.dataset.step || 0); const min = Number(input.min || 1); const max = input.max ? Number(input.max) : Infinity;
      input.value = Math.min(max, Math.max(min, Number(input.value || min) + step)); input.dispatchEvent(new Event('change', { bubbles: true })); return;
    }

    const cartStep = target.closest('[data-cart-change]');
    if (cartStep) {
      const item = cartStep.closest('[data-cart-line]'); const input = qs('[data-cart-quantity-input]', item); if (!item || !input) return;
      const next = Math.max(0, Number(input.value || 0) + Number(cartStep.dataset.cartChange));
      cartStep.setAttribute('aria-busy', 'true'); try { await changeCartLine(item.dataset.cartLine, next); } catch (_) {} return;
    }
    const cartRemove = target.closest('[data-cart-remove]');
    if (cartRemove) { event.preventDefault(); try { await changeCartLine(cartRemove.closest('[data-cart-line]').dataset.cartLine, 0); } catch (_) {} return; }

    const accordion = target.closest('[data-accordion-button]');
    if (accordion) { const item = accordion.closest('[data-accordion-item]'); const open = item.classList.toggle('is-open'); accordion.setAttribute('aria-expanded', String(open)); return; }

    const productTab = target.closest('[data-product-tab]');
    if (productTab) {
      const tabs = productTab.closest('[data-product-tabs]');
      qsa('[data-product-tab]', tabs).forEach((tab) => { const active = tab === productTab; tab.classList.toggle('is-active', active); tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1; });
      qsa('[data-product-tab-panel]', tabs).forEach((panel) => { panel.hidden = panel.dataset.productTabPanel !== productTab.dataset.productTab; });
      return;
    }

    const loadMore = target.closest('[data-load-more]');
    if (loadMore) { event.preventDefault(); await loadMoreProducts(loadMore); return; }

    const filter = target.closest('[data-filter-toggle]');
    if (filter) {
      const scope = filter.closest('.collection-grid-section') || document;
      const panel = qs('[data-filter-panel]', scope);
      if (!panel) return;
      const open = panel.classList.toggle('is-open');
      qsa('[data-filter-toggle]', scope).forEach((button) => button.setAttribute('aria-expanded', String(open)));
      document.body.classList.toggle('filters-open', open && panel.classList.contains('filters--drawer'));
      return;
    }

    const hotspot = target.closest('[data-hotspot-toggle]');
    if (hotspot) {
      const parent = hotspot.closest('.lookbook-hotspot'); const card = qs('[data-hotspot-card]', parent); const open = card.hidden;
      qsa('[data-hotspot-card]', hotspot.closest('.lookbook-frame')).forEach((item) => { if (item !== card) item.hidden = true; });
      card.hidden = !open; hotspot.setAttribute('aria-expanded', String(open)); return;
    }
    const hotspotClose = target.closest('[data-hotspot-close]');
    if (hotspotClose) { const parent = hotspotClose.closest('.lookbook-hotspot'); qs('[data-hotspot-card]', parent).hidden = true; qs('[data-hotspot-toggle]', parent)?.setAttribute('aria-expanded', 'false'); }
  });

  const cartQuantityTimers = new WeakMap();
  let cartNoteTimer;

  document.addEventListener('input', (event) => {
    const input = event.target.closest?.('[data-cart-page-quantity]');
    if (input) {
      const existing = cartQuantityTimers.get(input);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => input.dispatchEvent(new Event('change', { bubbles: true })), 450);
      cartQuantityTimers.set(input, timer);
      return;
    }

    const note = event.target.closest?.('[data-cart-note]');
    if (!note) return;
    clearTimeout(cartNoteTimer);
    const status = qs('[data-cart-note-status]', note.closest('[data-cart-page]') || document);
    if (status) status.textContent = '';
    cartNoteTimer = setTimeout(async () => {
      try {
        await updateCartNote(note.value);
        if (status) status.textContent = 'Saved';
      } catch (_) {
        if (status) status.textContent = 'Unable to save note';
      }
    }, 600);
  });

  document.addEventListener('change', async (event) => {
    const input = event.target.closest?.('[data-cart-quantity-input]');
    if (!input) return;
    const timer = cartQuantityTimers.get(input);
    if (timer) clearTimeout(timer);
    const item = input.closest('[data-cart-line]'); if (!item) return;
    input.setAttribute('aria-busy', 'true');
    try { await changeCartLine(item.dataset.cartLine, Math.max(0, Number(input.value || 0))); } catch (_) {
      input.removeAttribute('aria-busy');
    }
  });

  qsa('[data-recipient-toggle]').forEach((toggle) => {
    const root = toggle.closest('[data-recipient-form]'); const offset = qs('[data-recipient-offset]', root);
    const syncRecipient = () => {
      const fields = qs('[data-recipient-fields]', root); if (!fields) return;
      fields.hidden = !toggle.checked; qsa('[data-recipient-field]', root).forEach((field) => { field.disabled = !toggle.checked; });
      if (offset) { offset.disabled = !toggle.checked; offset.value = toggle.checked ? String(new Date().getTimezoneOffset()) : ''; }
    };
    syncRecipient(); toggle.addEventListener('change', syncRecipient);
  });

  document.addEventListener('keydown', (event) => {
    const productTab = event.target.closest?.('[data-product-tab]');
    if (productTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      const tabs = qsa('[data-product-tab]', productTab.closest('[data-product-tabs]'));
      if (tabs.length) {
        event.preventDefault();
        let index = tabs.indexOf(productTab);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = tabs.length - 1;
        else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[index].focus();
        tabs[index].click();
      }
      return;
    }
    if (event.key !== 'Escape') return;
    if (closeSearchDrawer()) return;
    if (closeMobileNav()) return;
    const desktopDropdown = event.target.closest?.('[data-nav-dropdown][open]') || qs('[data-nav-dropdown][open]');
    if (desktopDropdown) {
      desktopDropdown.removeAttribute('open');
      qs('[data-nav-summary]', desktopDropdown)?.focus();
    }
  });

  document.addEventListener('shopify:block:select', () => {
    window.requestAnimationFrame(() => initPersonalizationFonts(document));
  });

  document.addEventListener('shopify:section:select', () => {
    window.requestAnimationFrame(() => initPersonalizationFonts(document));
  });

  document.addEventListener('shopify:section:load', (event) => {
    qsa('[data-product-section]', event.target).forEach((section) => { initProductGallery(section); try { initProduct(section); } catch (_) {} });
    initSlideshows(event.target); initBeforeAfter(event.target); initRecentlyViewed(event.target); initPersonalizationFonts(event.target); initStickyHeaders(event.target); initReveal(event.target); initInfiniteScroll(event.target);
  });
  function initPromoDiscountCopy(scope = document) {
    qsa('[data-copy-discount]', scope).forEach((button) => {
      if (button.dataset.copyInitialized === 'true') return;
      button.dataset.copyInitialized = 'true';
      button.addEventListener('click', async () => {
        const code = button.dataset.code || '';
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code);
          const label = qs('[data-copy-discount-label]', button);
          if (label) label.textContent = button.dataset.copiedLabel || button.dataset.copyLabel || '';
        } catch (_) {}
      });
    });
  }

  initPromoDiscountCopy();


  // Faceted editorial navigation v1.5.7
  function rotateMenuFeatures(scope) {
    if (!scope) return;
    qsa('[data-menu-feature-rotator]', scope).forEach((rotator) => {
      const options = qsa('[data-menu-feature-option]', rotator);
      if (!options.length) return;

      const previous = Number.parseInt(rotator.dataset.featureIndex || '-1', 10);
      const next = options.length > 1 ? (previous + 1 + options.length) % options.length : 0;
      rotator.dataset.featureIndex = String(next);

      options.forEach((option, index) => {
        const active = index === next;
        option.hidden = !active;
        option.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
    });
  }

  function activateEditorialPreview(trigger) {
    if (!trigger) return;
    const menu = trigger.closest('[data-editorial-menu]');
    if (!menu) return;

    qsa('[data-editorial-trigger]', menu).forEach((item) => {
      item.setAttribute('aria-current', item === trigger ? 'true' : 'false');
    });

    const target = trigger.dataset.editorialTarget;
    if (target) {
      qsa('[data-editorial-product-panel]', menu).forEach((panel) => {
        const active = panel.dataset.editorialProductPanel === target;
        panel.classList.toggle('is-hidden', !active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      return;
    }

    const feature = qs('[data-editorial-feature]', menu);
    if (!feature) return;

    const image = qs('[data-editorial-image]', feature);
    const placeholder = qs('[data-editorial-placeholder]', feature);
    const heading = trigger.dataset.previewHeading || trigger.textContent.trim();
    const textValue = trigger.dataset.previewText || '';
    const eyebrowValue = trigger.dataset.previewEyebrow || '';
    const labelValue = trigger.dataset.previewLabel || 'Explore';
    const urlValue = trigger.dataset.previewUrl || trigger.getAttribute('href') || '#';
    const imageValue = trigger.dataset.previewImage || '';

    feature.setAttribute('href', urlValue);
    const eyebrow = qs('[data-editorial-eyebrow]', feature);
    if (eyebrow) eyebrow.textContent = eyebrowValue;
    const title = qs('[data-editorial-heading]', feature);
    if (title) title.textContent = heading;
    const copy = qs('[data-editorial-text]', feature);
    if (copy) {
      copy.textContent = textValue;
      copy.hidden = !textValue;
    }
    const label = qs('[data-editorial-link-label]', feature);
    if (label) label.textContent = labelValue;

    if (image) {
      if (imageValue) {
        if (image.getAttribute('src') !== imageValue) image.setAttribute('src', imageValue);
        image.alt = heading;
        image.hidden = false;
        placeholder?.classList.add('is-hidden');
      } else {
        image.hidden = true;
        placeholder?.classList.remove('is-hidden');
      }
    }
  }

  function initFacetedMenus(scope = document) {
    qsa('[data-nav-dropdown]', scope).forEach((details) => {
      if (details.dataset.facetedMenuInitialized === 'true') return;
      details.dataset.facetedMenuInitialized = 'true';

      const nav = details.closest('[data-desktop-menu]');
      const header = details.closest('.site-header');
      const panel = qs('.nav-dropdown__panel', details);
      const mode = details.dataset.menuMode || 'dropdown';
      const activation = nav?.dataset.menuActivation || 'click';
      let closeTimer;

      const syncPanelTop = () => {
        if (!panel || !header) return;
        const headerRect = header.getBoundingClientRect();
        const bottom = Math.max(0, headerRect.bottom);

        if (mode === 'dropdown') {
          const detailsRect = details.getBoundingClientRect();
          panel.style.setProperty('--faceted-dropdown-top', Math.max(0, bottom - detailsRect.top) + 'px');
          return;
        }

        if (['mega', 'editorial', 'drawer'].includes(mode)) {
          panel.style.setProperty('--faceted-menu-max-height', Math.max(260, window.innerHeight - bottom) + 'px');
        }
      };

      const closeSiblings = () => {
        qsa('[data-nav-dropdown]', nav || document).forEach((item) => {
          if (item !== details) item.removeAttribute('open');
        });
      };

      const openMenu = () => {
        clearTimeout(closeTimer);
        closeSiblings();
        details.setAttribute('open', '');
        syncPanelTop();
      };

      const scheduleClose = () => {
        clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => details.removeAttribute('open'), 220);
      };

      details.addEventListener('toggle', () => {
        if (details.open) {
          closeSiblings();
          syncPanelTop();
          rotateMenuFeatures(details);
        }
      });

      if (activation === 'hover' && window.matchMedia('(hover:hover) and (pointer:fine)').matches) {
        details.addEventListener('mouseenter', openMenu);
        details.addEventListener('mouseleave', scheduleClose);
        panel?.addEventListener('mouseenter', () => clearTimeout(closeTimer));
        panel?.addEventListener('mouseleave', scheduleClose);
      }

      details.addEventListener('focusin', openMenu);
      details.addEventListener('focusout', (event) => {
        if (!details.contains(event.relatedTarget)) scheduleClose();
      });

      qsa('[data-editorial-trigger]', details).forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => activateEditorialPreview(trigger));
        trigger.addEventListener('focus', () => activateEditorialPreview(trigger));
      });

      const firstTrigger = qs('[data-editorial-trigger]', details);
      if (firstTrigger) activateEditorialPreview(firstTrigger);

      window.addEventListener('resize', () => { if (details.open) syncPanelTop(); }, { passive:true });
      window.addEventListener('scroll', () => { if (details.open) syncPanelTop(); }, { passive:true });
    });
  }

  initFacetedMenus();

  document.addEventListener('shopify:section:load', (event) => initFacetedMenus(event.target));

  document.addEventListener('click', (event) => {
    if (event.target.closest('.site-header')) return;
    qsa('[data-nav-dropdown][open]').forEach((details) => details.removeAttribute('open'));
  });

})();
